import React from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../notifications';
import { Spinner, ToggleField } from '../../../shared/components';
import {
  TikTokSettings,
  TikTokTestSendResult,
  TikTokWebhookRegistration,
  UpdateTikTokSettingsRequest,
} from '../../companies/types/Company';
import {
  getTikTokSettings,
  getTikTokWebhookRegistration,
  registerTikTokWebhook,
  startTikTokOAuth,
  updateTikTokSettings,
} from '../../companies/services/companyService';
import {
  createTikTokChannelAccount,
  getTikTokChannelAccountSettings,
  sendTikTokChannelAccountTestMessage,
  updateTikTokChannelAccountCredentials,
} from '../services/channelAccountService';
import { ChannelAccountTikTokSettings } from '../types/channelAccount';
import { useChannelAccounts } from '../hooks/useChannelAccounts';
import { ChannelAccountBar } from './ChannelAccountBar';
import { AddTikTokAccountForm } from './AddTikTokAccountForm';
import { ConnectorDoneLabel, ConnectorFieldStatus } from './ConnectorFieldStatus';

const getDefaultTikTokOAuthFrontendReturnUrl = (): string =>
  `${window.location.origin.replace(/\/$/, '')}/admin/connectors?tab=tiktok`;

const getApiBaseUrl = (): string => (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

const getDefaultTikTokOAuthRedirectUri = (webhookUrl?: string, suggestedRedirectUri?: string): string => {
  if (suggestedRedirectUri?.trim()) {
    return suggestedRedirectUri.trim();
  }

  if (webhookUrl) {
    try {
      const parsed = new URL(webhookUrl);
      return `${parsed.origin}/api/v1/tiktok/settings/oauth/callback`;
    } catch {
      // fall through
    }
  }

  const apiBase = getApiBaseUrl();
  if (!apiBase) {
    return '';
  }

  return `${apiBase}/api/v1/tiktok/settings/oauth/callback`;
};

const emptyTikTokSettingsForm: UpdateTikTokSettingsRequest = {
  apiBaseUrl: 'https://business-api.tiktok.com/open_api/v1.3',
  appId: '',
  appSecret: '',
  businessOpenId: '',
  accessToken: '',
  oauthRedirectUri: '',
  oauthFrontendReturnUrl: getDefaultTikTokOAuthFrontendReturnUrl(),
  validateSignature: false,
};

const formatDateTime = (value?: string): string => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

interface TikTokConnectorTabProps {
  canManage: boolean;
  onOAuthReturn?: (status: string) => void;
}

export const TikTokConnectorTab: React.FC<TikTokConnectorTabProps> = ({ canManage, onOAuthReturn }) => {
  const { t } = useTranslation(['companies', 'admin']);
  const { showToast } = useToast();
  const tiktokChannelAccounts = useChannelAccounts('TikTok', canManage, 'cloud');

  const [loadingSettings, setLoadingSettings] = React.useState(false);
  const [savingSettings, setSavingSettings] = React.useState(false);
  const [connectingOAuth, setConnectingOAuth] = React.useState(false);
  const [registeringWebhook, setRegisteringWebhook] = React.useState(false);
  const [loadingWebhook, setLoadingWebhook] = React.useState(false);
  const [testingSend, setTestingSend] = React.useState(false);
  const [showAddTikTokAccount, setShowAddTikTokAccount] = React.useState(false);
  const [testConversationId, setTestConversationId] = React.useState('');
  const [testMessage, setTestMessage] = React.useState('Hello from ShahAgent TikTok test');
  const [latestTestSendResult, setLatestTestSendResult] = React.useState<TikTokTestSendResult | null>(null);
  const [tenantSettings, setTenantSettings] = React.useState<TikTokSettings | null>(null);
  const [accountSettings, setAccountSettings] = React.useState<ChannelAccountTikTokSettings | null>(null);
  const [settingsForm, setSettingsForm] = React.useState<UpdateTikTokSettingsRequest>(emptyTikTokSettingsForm);
  const [settingsError, setSettingsError] = React.useState<string | null>(null);
  const [settingsNotice, setSettingsNotice] = React.useState<string | null>(null);
  const [webhookUrl, setWebhookUrl] = React.useState('');
  const [webhookRegistration, setWebhookRegistration] = React.useState<TikTokWebhookRegistration | null>(null);

  const resolveOAuthRedirectUri = React.useCallback((): string => {
    const manual = settingsForm.oauthRedirectUri?.trim();
    if (manual) {
      return manual;
    }

    const suggested = tenantSettings?.suggestedOAuthRedirectUri?.trim();
    if (suggested) {
      return suggested;
    }

    const stored = tenantSettings?.oauthRedirectUri?.trim();
    if (stored) {
      return stored;
    }

    return getDefaultTikTokOAuthRedirectUri(webhookUrl, tenantSettings?.suggestedOAuthRedirectUri);
  }, [tenantSettings, settingsForm.oauthRedirectUri, webhookUrl]);

  const recommendedOAuthRedirectUri = React.useMemo(
    () => getDefaultTikTokOAuthRedirectUri(webhookUrl, tenantSettings?.suggestedOAuthRedirectUri),
    [tenantSettings?.suggestedOAuthRedirectUri, webhookUrl],
  );

  const hasOAuthRedirectUriMismatch = React.useMemo(() => {
    const active = resolveOAuthRedirectUri();
    const recommended = recommendedOAuthRedirectUri.trim();
    const stored = tenantSettings?.oauthRedirectUri?.trim();
    return Boolean(
      recommended
      && stored
      && stored !== recommended
      && active === recommended,
    );
  }, [recommendedOAuthRedirectUri, resolveOAuthRedirectUri, tenantSettings?.oauthRedirectUri]);

  const hasSettingsChanges = React.useMemo(() => {
    const tenantChanged = !tenantSettings
      ? Boolean(settingsForm.appId?.trim())
        || Boolean(settingsForm.appSecret?.trim())
        || Boolean(settingsForm.oauthRedirectUri?.trim())
        || Boolean(settingsForm.oauthFrontendReturnUrl?.trim())
      : Boolean(settingsForm.appId?.trim())
        || Boolean(settingsForm.appSecret?.trim())
        || (settingsForm.oauthRedirectUri?.trim() || '') !== (tenantSettings.oauthRedirectUri || '')
        || (settingsForm.oauthFrontendReturnUrl?.trim() || getDefaultTikTokOAuthFrontendReturnUrl()) !== (tenantSettings.oauthFrontendReturnUrl || getDefaultTikTokOAuthFrontendReturnUrl());

    const accountChanged = !accountSettings
      ? Boolean(settingsForm.accessToken?.trim())
        || Boolean(settingsForm.businessOpenId?.trim())
        || (settingsForm.apiBaseUrl?.trim() || 'https://business-api.tiktok.com/open_api/v1.3') !== 'https://business-api.tiktok.com/open_api/v1.3'
        || Boolean(settingsForm.validateSignature)
      : Boolean(settingsForm.accessToken?.trim())
        || (settingsForm.businessOpenId?.trim() || '') !== (accountSettings.businessOpenId || '')
        || (settingsForm.apiBaseUrl?.trim() || 'https://business-api.tiktok.com/open_api/v1.3') !== (accountSettings.apiBaseUrl || 'https://business-api.tiktok.com/open_api/v1.3')
        || Boolean(settingsForm.validateSignature) !== accountSettings.validateSignature;

    return tenantChanged || accountChanged;
  }, [accountSettings, tenantSettings, settingsForm]);

  const loadTenantSettings = React.useCallback(async () => {
    if (!canManage) {
      return;
    }

    setSettingsError(null);
    setSettingsNotice(null);

    try {
      const data = await getTikTokSettings();
      setTenantSettings(data);
      const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
      const defaultWebhookUrl = data.suggestedWebhookUrl
        || (apiBase ? `${apiBase}/api/v1/tiktok/webhook` : '');
      setWebhookUrl(defaultWebhookUrl);
      setSettingsForm(prev => ({
        ...prev,
        appId: '',
        appSecret: '',
        oauthRedirectUri: '',
        oauthFrontendReturnUrl: data.oauthFrontendReturnUrl || data.suggestedOAuthFrontendReturnUrl || getDefaultTikTokOAuthFrontendReturnUrl(),
      }));

      if (data.hasAppId && data.hasAppSecret) {
        setLoadingWebhook(true);
        try {
          const registration = await getTikTokWebhookRegistration();
          setWebhookRegistration(registration);
        } catch {
          setWebhookRegistration(null);
        } finally {
          setLoadingWebhook(false);
        }
      }
    } catch (error) {
      setSettingsError(error instanceof Error ? error.message : t('failedToLoadTikTokSettings'));
    }
  }, [canManage, t]);

  const loadAccountSettings = React.useCallback(async (accountId: string) => {
    if (!canManage) {
      return;
    }

    setLoadingSettings(true);
    setSettingsError(null);
    setSettingsNotice(null);

    try {
      const data = await getTikTokChannelAccountSettings(accountId);
      setAccountSettings(data);
      setLatestTestSendResult(null);
      setSettingsForm(prev => ({
        ...prev,
        accessToken: '',
        businessOpenId: data.businessOpenId || '',
        apiBaseUrl: data.apiBaseUrl || 'https://business-api.tiktok.com/open_api/v1.3',
        validateSignature: data.validateSignature,
      }));
    } catch (error) {
      setSettingsError(error instanceof Error ? error.message : t('failedToLoadTikTokSettings'));
    } finally {
      setLoadingSettings(false);
    }
  }, [canManage, t]);

  React.useEffect(() => {
    if (!canManage) {
      return;
    }

    void loadTenantSettings();
  }, [canManage, loadTenantSettings]);

  React.useEffect(() => {
    const accountId = tiktokChannelAccounts.selectedAccountId;
    if (!accountId) {
      setAccountSettings(null);
      return;
    }

    void loadAccountSettings(accountId);
  }, [loadAccountSettings, tiktokChannelAccounts.selectedAccountId]);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthStatus = params.get('tiktokOAuth');
    if (!oauthStatus) {
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.delete('tiktokOAuth');
    window.history.replaceState({}, '', url.toString());

    if (oauthStatus === 'success') {
      showToast(t('tiktokOAuthSuccess'), 'success');
      void loadTenantSettings();
      void tiktokChannelAccounts.reload();
    } else if (oauthStatus === 'ungranted_scopes') {
      showToast(t('tiktokOAuthUngrantedScopes'), 'error');
    } else {
      showToast(t('tiktokOAuthFailed'), 'error');
    }

    onOAuthReturn?.(oauthStatus);
  }, [loadTenantSettings, onOAuthReturn, showToast, t, tiktokChannelAccounts]);

  const saveSettings = async () => {
    const selectedAccount = tiktokChannelAccounts.selectedAccount;
    const isInitialSetup = tiktokChannelAccounts.accounts.length === 0;

    setSavingSettings(true);
    setSettingsError(null);
    setSettingsNotice(null);

    try {
      const updatedTenant = await updateTikTokSettings({
        appId: settingsForm.appId?.trim() || undefined,
        appSecret: settingsForm.appSecret?.trim() || undefined,
        oauthRedirectUri: settingsForm.oauthRedirectUri?.trim() || undefined,
        oauthFrontendReturnUrl: settingsForm.oauthFrontendReturnUrl?.trim() || undefined,
      });
      setTenantSettings(updatedTenant);

      if (isInitialSetup) {
        if (!settingsForm.businessOpenId?.trim() || !settingsForm.accessToken?.trim()) {
          throw new Error(t('tiktokAccountCredentialsRequired', { defaultValue: 'Business open ID and access token are required to create the TikTok account.' }));
        }

        await createTikTokChannelAccount({
          displayName: 'TikTok',
          businessOpenId: settingsForm.businessOpenId.trim(),
          accessToken: settingsForm.accessToken.trim(),
          appId: settingsForm.appId?.trim() || undefined,
          appSecret: settingsForm.appSecret?.trim() || undefined,
          apiBaseUrl: settingsForm.apiBaseUrl?.trim() || undefined,
          validateSignature: settingsForm.validateSignature,
          isDefault: true,
        });
        await tiktokChannelAccounts.reload();
      } else if (selectedAccount) {
        await updateTikTokChannelAccountCredentials(selectedAccount.id, {
          businessOpenId: settingsForm.businessOpenId?.trim() || undefined,
          accessToken: settingsForm.accessToken?.trim() || undefined,
          appId: settingsForm.appId?.trim() || undefined,
          appSecret: settingsForm.appSecret?.trim() || undefined,
          apiBaseUrl: settingsForm.apiBaseUrl?.trim() || undefined,
          validateSignature: settingsForm.validateSignature,
        });
        await loadAccountSettings(selectedAccount.id);
        await tiktokChannelAccounts.reload();
      }

      setSettingsForm(prev => ({
        ...prev,
        appId: '',
        appSecret: '',
        accessToken: '',
        oauthRedirectUri: updatedTenant.oauthRedirectUri || updatedTenant.suggestedOAuthRedirectUri || '',
        oauthFrontendReturnUrl: updatedTenant.oauthFrontendReturnUrl || updatedTenant.suggestedOAuthFrontendReturnUrl || getDefaultTikTokOAuthFrontendReturnUrl(),
      }));
      setSettingsNotice(t('tiktokSettingsSaved'));
      showToast(t('tiktokSettingsSaved'), 'success');
      setLatestTestSendResult(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToSaveTikTokSettings');
      setSettingsError(message);
      showToast(message, 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  const connectTikTokOAuth = async () => {
    const hasAppId = Boolean(settingsForm.appId?.trim() || tenantSettings?.hasAppId);
    const hasAppSecret = Boolean(settingsForm.appSecret?.trim() || tenantSettings?.hasAppSecret);
    const oauthRedirectUri = resolveOAuthRedirectUri();

    if (!hasAppId || !hasAppSecret || !oauthRedirectUri) {
      const message = t('tiktokOAuthSaveFirst');
      setSettingsError(message);
      showToast(message, 'error');
      return;
    }

    setConnectingOAuth(true);
    setSettingsError(null);
    setSettingsNotice(null);

    try {
      await updateTikTokSettings({
        appId: settingsForm.appId?.trim() || undefined,
        appSecret: settingsForm.appSecret?.trim() || undefined,
        oauthRedirectUri,
        oauthFrontendReturnUrl: settingsForm.oauthFrontendReturnUrl?.trim()
          || tenantSettings?.oauthFrontendReturnUrl
          || getDefaultTikTokOAuthFrontendReturnUrl(),
      });
      await loadTenantSettings();

      const result = await startTikTokOAuth();
      if (!result.authorizeUrl) {
        throw new Error(t('tiktokOAuthFailed'));
      }

      window.location.assign(result.authorizeUrl);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('tiktokOAuthFailed');
      setSettingsError(message);
      showToast(message, 'error');
      setConnectingOAuth(false);
    }
  };

  const registerWebhook = async () => {
    setRegisteringWebhook(true);
    setSettingsError(null);
    setSettingsNotice(null);

    try {
      const result = await registerTikTokWebhook();
      setWebhookRegistration(result);
      setSettingsNotice(t('tiktokWebhookRegistered'));
      showToast(t('tiktokWebhookRegisteredToast', {
        callbackUrl: result.registeredCallbackUrl || result.callbackUrl,
      }), 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToRegisterTikTokWebhook');
      setSettingsError(message);
      showToast(message, 'error');
    } finally {
      setRegisteringWebhook(false);
    }
  };

  const sendTestMessage = async () => {
    const selectedAccount = tiktokChannelAccounts.selectedAccount;
    if (!selectedAccount) {
      return;
    }

    if (!testConversationId.trim()) {
      const message = t('tiktokTestConversationIdRequired');
      setSettingsError(message);
      setSettingsNotice(null);
      showToast(message, 'error');
      return;
    }

    if (!testMessage.trim()) {
      const message = t('tiktokTestMessageRequired');
      setSettingsError(message);
      setSettingsNotice(null);
      showToast(message, 'error');
      return;
    }

    setTestingSend(true);
    setSettingsError(null);
    setSettingsNotice(null);

    try {
      const result = await sendTikTokChannelAccountTestMessage(selectedAccount.id, {
        conversationId: testConversationId.trim(),
        text: testMessage.trim(),
      });
      setLatestTestSendResult(result);
      setSettingsNotice(t('tiktokTestSendSucceeded', { conversationId: result.conversationId }));
      showToast(t('tiktokTestSendSucceededToast', {
        conversationId: result.conversationId,
        messageId: result.messageId,
        sentAt: formatDateTime(result.sentAtUtc),
      }), 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToSendTikTokTestMessage');
      setSettingsError(message);
      showToast(message, 'error');
    } finally {
      setTestingSend(false);
    }
  };

  if (!canManage) {
    return null;
  }

  return (
    <section className="company-section">
      <div className="company-section-head">
        <h3>{t('tiktokSettingsHeading')}</h3>
      </div>

      <p style={{ color: '#516173', marginTop: 0 }}>
        {t('tiktokSettingsDescription')}
      </p>

      {settingsError ? <div className="error">{settingsError}</div> : null}
      {settingsNotice ? <div className="success">{settingsNotice}</div> : null}

      <ChannelAccountBar
        channel="TikTok"
        connectionKind="cloud"
        accounts={tiktokChannelAccounts.accounts}
        limits={tiktokChannelAccounts.limits}
        loading={tiktokChannelAccounts.loading}
        error={tiktokChannelAccounts.error}
        selectedAccountId={tiktokChannelAccounts.selectedAccountId}
        onSelectAccount={tiktokChannelAccounts.setSelectedAccountId}
        onRefresh={tiktokChannelAccounts.reload}
        canManage={canManage}
        showAddForm={showAddTikTokAccount}
        onToggleAddForm={() => setShowAddTikTokAccount(previous => !previous)}
        onAccountUpdated={() => {
          const accountId = tiktokChannelAccounts.selectedAccountId;
          if (accountId) {
            void loadAccountSettings(accountId);
          }
        }}
      />

      {showAddTikTokAccount ? (
        <AddTikTokAccountForm
          canManage={canManage}
          onCancel={() => setShowAddTikTokAccount(false)}
          onCreated={() => {
            setShowAddTikTokAccount(false);
            void tiktokChannelAccounts.reload();
            showToast(t('channelAccountCreated', { ns: 'companies' }), 'success');
          }}
        />
      ) : null}

      {tiktokChannelAccounts.accounts.length === 0 ? (
        <p style={{ color: '#516173', marginTop: 16 }}>
          {t('channelAccountCreateAccountForSettings', { ns: 'companies' })}
        </p>
      ) : null}

      {loadingSettings && tiktokChannelAccounts.selectedAccountId ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
          <Spinner text={t('loadingTikTokSettings')} />
        </div>
      ) : (
        <>
          <div className="company-section" style={{ marginBottom: 16 }}>
            <div className="company-section-head">
              <h3>{t('tiktokOAuthConnectionHeading')}</h3>
            </div>
            <p style={{ color: '#516173', marginTop: 0 }}>
              {tenantSettings?.connectedViaOAuth
                ? t('tiktokOAuthConfigured')
                : t('tiktokOAuthHelp')}
            </p>
            {recommendedOAuthRedirectUri ? (
              <div style={{ color: '#516173', fontSize: 13, marginBottom: 12 }}>
                <div>{t('tiktokOAuthRedirectUriActive')}</div>
                <code style={{ display: 'block', fontSize: 12, wordBreak: 'break-all', marginTop: 6 }}>
                  {resolveOAuthRedirectUri() || recommendedOAuthRedirectUri}
                </code>
              </div>
            ) : (
              <div className="error" style={{ marginBottom: 12 }}>
                {t('tiktokOAuthRedirectUriMissingBackendUrl')}
              </div>
            )}
            {hasOAuthRedirectUriMismatch ? (
              <div style={{ color: '#516173', fontSize: 13, marginBottom: 12 }}>
                {t('tiktokOAuthRedirectUriMismatch')}
              </div>
            ) : null}
            <div className="company-actions-inline" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={connectTikTokOAuth}
                disabled={connectingOAuth || savingSettings || !recommendedOAuthRedirectUri}
              >
                {connectingOAuth
                  ? t('tiktokOAuthConnecting')
                  : tenantSettings?.connectedViaOAuth
                    ? t('tiktokOAuthReconnect')
                    : t('tiktokOAuthConnect')}
              </button>
            </div>
          </div>

          <div className="company-section" style={{ marginBottom: 16 }}>
            <div className="company-section-head">
              <h3>{t('tiktokOAuthSettingsHeading')}</h3>
            </div>
            <p style={{ color: '#516173', marginTop: 0 }}>
              {t('tiktokOAuthSettingsHelp')}
            </p>
            <div style={{ color: '#516173', fontSize: 13, marginBottom: 12 }}>
              {t('tiktokOAuthRedirectUriHelp')}
            </div>
            <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
              <div style={{ color: '#516173', fontSize: 13 }}>
                {t('tiktokOAuthRedirectUriRecommended')}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <code style={{ fontSize: 12, wordBreak: 'break-all' }}>
                  {recommendedOAuthRedirectUri || t('telegramStatusNotAvailable')}
                </code>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={!recommendedOAuthRedirectUri}
                  onClick={() => {
                    if (!recommendedOAuthRedirectUri) {
                      return;
                    }

                    void navigator.clipboard.writeText(recommendedOAuthRedirectUri);
                    showToast(t('tiktokOAuthRedirectUriCopied'), 'success');
                  }}
                >
                  {t('tiktokOAuthRedirectUriCopy')}
                </button>
              </div>
            </div>
            <div className="company-form-grid">
              <label>
                {t('tiktokAppId')}
                <input
                  className="text-input"
                  value={settingsForm.appId || ''}
                  onChange={event => setSettingsForm(prev => ({ ...prev, appId: event.target.value }))}
                  placeholder={tenantSettings?.hasAppId ? t('tiktokAppIdConfigured') : t('tiktokAppIdPlaceholder')}
                />
              </label>

              <label>
                {t('tiktokAppSecret')}
                <input
                  className="text-input"
                  type="password"
                  value={settingsForm.appSecret || ''}
                  onChange={event => setSettingsForm(prev => ({ ...prev, appSecret: event.target.value }))}
                  placeholder={tenantSettings?.hasAppSecret ? t('tiktokAppSecretConfigured') : t('tiktokAppSecretPlaceholder')}
                  autoComplete="new-password"
                />
              </label>

              <label>
                {t('tiktokOAuthRedirectUri')}
                <input
                  className="text-input"
                  value={settingsForm.oauthRedirectUri || ''}
                  onChange={event => setSettingsForm(prev => ({ ...prev, oauthRedirectUri: event.target.value }))}
                  placeholder={recommendedOAuthRedirectUri || 'https://your-backend-host/api/v1/tiktok/settings/oauth/callback'}
                />
              </label>

              <label>
                {t('tiktokOAuthFrontendReturnUrl')}
                <input
                  className="text-input"
                  value={settingsForm.oauthFrontendReturnUrl || ''}
                  onChange={event => setSettingsForm(prev => ({ ...prev, oauthFrontendReturnUrl: event.target.value }))}
                  placeholder={getDefaultTikTokOAuthFrontendReturnUrl()}
                />
              </label>
            </div>
          </div>

          <div className="company-section" style={{ marginBottom: 16 }}>
            <div className="company-section-head">
              <h3>{t('tiktokAdvancedSettingsHeading')}</h3>
            </div>
            <div className="company-form-grid">
              <label>
                {t('tiktokApiBaseUrl')}
                <input
                  className="text-input"
                  value={settingsForm.apiBaseUrl || ''}
                  onChange={event => setSettingsForm(prev => ({ ...prev, apiBaseUrl: event.target.value }))}
                  placeholder="https://business-api.tiktok.com/open_api/v1.3"
                />
              </label>

              <label>
                {t('tiktokBusinessOpenId')}
                <input
                  className="text-input"
                  value={settingsForm.businessOpenId || ''}
                  onChange={event => setSettingsForm(prev => ({ ...prev, businessOpenId: event.target.value }))}
                  placeholder={accountSettings?.businessOpenId || tenantSettings?.businessOpenId || 'Auto-filled after OAuth'}
                />
              </label>

              <label>
                {t('tiktokAccessToken')}
                <input
                  className="text-input"
                  type="password"
                  value={settingsForm.accessToken || ''}
                  onChange={event => setSettingsForm(prev => ({ ...prev, accessToken: event.target.value }))}
                  placeholder={accountSettings?.hasAccessToken || tenantSettings?.hasAccessToken ? t('tiktokAccessTokenConfigured') : t('tiktokAccessTokenPlaceholder')}
                  autoComplete="new-password"
                />
              </label>

              <ToggleField
                label={t('tiktokValidateSignature')}
                checked={Boolean(settingsForm.validateSignature)}
                onChange={value => setSettingsForm(prev => ({ ...prev, validateSignature: value }))}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gap: 6, marginTop: 12, color: '#516173', fontSize: 13 }}>
            <div>{t('tiktokAppSecretHint')}</div>
            <div>{t('tiktokSettingsStatus', {
              appId: tenantSettings?.hasAppId ? t('configured') : t('missing'),
              appSecret: tenantSettings?.hasAppSecret ? t('configured') : t('missing'),
              accessToken: accountSettings?.hasAccessToken || tenantSettings?.hasAccessToken ? t('configured') : t('missing'),
            })}</div>
          </div>

          <div className="telegram-status-grid">
            <div className="telegram-status-card">
              <div className="telegram-status-card-title">{t('tiktokStatusConnection')}</div>
              <dl className="telegram-status-list">
                <div>
                  <dt>{t('tiktokStatusUsername')}</dt>
                  <dd>{tenantSettings?.businessUsername || t('telegramStatusNotAvailable')}</dd>
                </div>
                <div>
                  <dt>{t('tiktokStatusBusinessOpenId')}</dt>
                  <dd>{accountSettings?.businessOpenId || settingsForm.businessOpenId || tenantSettings?.businessOpenId || t('telegramStatusNotAvailable')}</dd>
                </div>
                <div>
                  <dt>{t('tiktokAppId')}</dt>
                  <dd><ConnectorFieldStatus done={Boolean(tenantSettings?.hasAppId)} /></dd>
                </div>
                <div>
                  <dt>{t('tiktokAppSecret')}</dt>
                  <dd><ConnectorFieldStatus done={Boolean(tenantSettings?.hasAppSecret)} /></dd>
                </div>
                <div>
                  <dt>{t('tiktokStatusAccessToken')}</dt>
                  <dd><ConnectorFieldStatus done={Boolean(accountSettings?.hasAccessToken || tenantSettings?.hasAccessToken)} /></dd>
                </div>
                <div>
                  <dt>{t('tiktokStatusTokenExpiry')}</dt>
                  <dd>{formatDateTime(tenantSettings?.accessTokenExpiresAt)}</dd>
                </div>
                <div>
                  <dt>{t('tiktokStatusLastRefresh')}</dt>
                  <dd>{formatDateTime(tenantSettings?.accessTokenRefreshedAt)}</dd>
                </div>
                <div>
                  <dt>{t('tiktokStatusConnectedViaOAuth')}</dt>
                  <dd><ConnectorFieldStatus done={Boolean(tenantSettings?.connectedViaOAuth)} variant="enabled" /></dd>
                </div>
                <div>
                  <dt>{t('tiktokStatusOAuthRedirectUri')}</dt>
                  <dd>{resolveOAuthRedirectUri() || recommendedOAuthRedirectUri || t('telegramStatusNotAvailable')}</dd>
                </div>
              </dl>
            </div>

            <div className="telegram-status-card">
              <div className="telegram-status-card-title">{t('tiktokStatusWebhook')}</div>
              <dl className="telegram-status-list">
                <div>
                  <dt>{t('tiktokStatusWebhookUrl')}</dt>
                  <dd>{webhookUrl || t('telegramStatusNotAvailable')}</dd>
                </div>
                <div>
                  <dt>{t('tiktokStatusSignatureValidation')}</dt>
                  <dd><ConnectorFieldStatus done={Boolean(accountSettings?.validateSignature ?? tenantSettings?.validateSignature)} variant="enabled" /></dd>
                </div>
                {loadingWebhook ? (
                  <div>
                    <dt>{t('tiktokStatusWebhookRegistration')}</dt>
                    <dd>{t('loadingTikTokWebhookRegistration')}</dd>
                  </div>
                ) : webhookRegistration?.success ? (
                  <div>
                    <dt>{t('tiktokStatusWebhookRegistration')}</dt>
                    <dd>
                      <ConnectorDoneLabel>
                        {webhookRegistration.registeredCallbackUrl || webhookRegistration.callbackUrl}
                      </ConnectorDoneLabel>
                    </dd>
                  </div>
                ) : null}
              </dl>
            </div>

            <div className="telegram-status-card">
              <div className="telegram-status-card-title">{t('tiktokStatusTestSend')}</div>
              {latestTestSendResult ? (
                <dl className="telegram-status-list">
                  <div>
                    <dt>{t('tiktokTestConversationId')}</dt>
                    <dd>{latestTestSendResult.conversationId}</dd>
                  </div>
                  <div>
                    <dt>{t('messengerStatusMessageId')}</dt>
                    <dd>{latestTestSendResult.messageId}</dd>
                  </div>
                  <div>
                    <dt>{t('telegramStatusSentAt')}</dt>
                    <dd>{formatDateTime(latestTestSendResult.sentAtUtc)}</dd>
                  </div>
                </dl>
              ) : (
                <div className="telegram-status-empty">{t('tiktokStatusNoTestSend')}</div>
              )}
            </div>
          </div>

          <div className="company-actions-inline" style={{ marginTop: 16 }}>
            <button type="button" className="btn btn-primary" onClick={saveSettings} disabled={savingSettings || !hasSettingsChanges}>
              {savingSettings ? t('savingEllipsis', { ns: 'admin' }) : t('saveTikTokSettings')}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={registerWebhook}
              disabled={registeringWebhook || !tenantSettings?.hasAppId || !tenantSettings?.hasAppSecret}
            >
              {registeringWebhook ? t('savingEllipsis', { ns: 'admin' }) : t('registerTikTokWebhook')}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setSettingsForm({
                  appId: '',
                  appSecret: '',
                  accessToken: '',
                  businessOpenId: accountSettings?.businessOpenId || tenantSettings?.businessOpenId || '',
                  oauthRedirectUri: tenantSettings?.oauthRedirectUri || tenantSettings?.suggestedOAuthRedirectUri || '',
                  oauthFrontendReturnUrl: tenantSettings?.oauthFrontendReturnUrl || tenantSettings?.suggestedOAuthFrontendReturnUrl || getDefaultTikTokOAuthFrontendReturnUrl(),
                  apiBaseUrl: accountSettings?.apiBaseUrl || tenantSettings?.apiBaseUrl || 'https://business-api.tiktok.com/open_api/v1.3',
                  validateSignature: accountSettings?.validateSignature ?? tenantSettings?.validateSignature ?? false,
                });
                setSettingsError(null);
                setSettingsNotice(null);
              }}
              disabled={savingSettings}
            >
              {t('resetTikTokForm')}
            </button>
          </div>

          <div className="company-section" style={{ marginTop: 16 }}>
            <div className="company-section-head">
              <h3>{t('tiktokTestSendSection')}</h3>
            </div>
            <div className="company-form-grid">
              <label>
                {t('tiktokTestConversationId')}
                <input
                  className="text-input"
                  value={testConversationId}
                  onChange={event => setTestConversationId(event.target.value)}
                  placeholder="Conversation ID from an inbound webhook event"
                />
              </label>
              <label>
                {t('tiktokTestMessage')}
                <input
                  className="text-input"
                  value={testMessage}
                  onChange={event => setTestMessage(event.target.value)}
                />
              </label>
            </div>
            <div style={{ color: '#516173', fontSize: 13, marginTop: 8 }}>
              {t('tiktokTestSendHint')}
            </div>
            <div className="company-actions-inline" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={sendTestMessage}
                disabled={testingSend || !(accountSettings?.hasAccessToken || tenantSettings?.hasAccessToken) || !testConversationId.trim() || !testMessage.trim()}
              >
                {testingSend ? t('savingEllipsis', { ns: 'admin' }) : t('sendTikTokTestMessage')}
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
};
